"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  computePower,
  lawExpression,
  type PowerComputation,
  type PowerNote,
  type PowerView,
  powerWorking,
  summarizePower,
} from "@/lib/tool-kit/arithmetic/power-view";

/** English strings. A translated content file overrides these through `ui`. */
export const EXPONENT_CALCULATOR_STRINGS = {
  baseLabel: "Base",
  baseHint: "A whole number, decimal or fraction such as 2, −1.5 or 3/4.",
  exponentLabel: "Exponent",
  exponentHint: "Whole, negative, decimal or fraction: 10, −3, 0.5 or 2/3.",
  secondLabel: "Second exponent (for the exponent laws)",
  secondHint: "Used only by the laws table below; whole numbers.",
  placesLabel: "Decimal places for irrational results",
  results: "Results",
  result: "Result",
  decimal: "Decimal",
  scientific: "Scientific notation",
  digits: "Digits in the result",
  approxDigits: "About {count} digits",
  allDigits: "All the digits",
  copyDigits: "Copy digits",
  copyResult: "Copy result",
  copied: "Copied!",
  workingHeading: "The working",
  lawsHeading: "The exponent laws on your numbers",
  lawsIntro:
    "With your base and the two whole-number exponents, both sides of each law come out the same.",
  lawsNeedWhole:
    "Enter whole numbers for both exponents (each between −60 and 60) to see the exponent laws checked on your base.",
  columnLaw: "Law",
  columnRule: "Rule",
  columnNumbers: "With your numbers",
  lawProduct: "Product rule",
  lawQuotient: "Quotient rule",
  lawPower: "Power of a power",
  lawNegative: "Negative exponent",
  lawZero: "Zero exponent",
  ruleProduct: "aᵐ × aⁿ = aᵐ⁺ⁿ",
  ruleQuotient: "aᵐ ÷ aⁿ = aᵐ⁻ⁿ",
  rulePower: "(aᵐ)ⁿ = aᵐⁿ",
  ruleNegative: "a⁻ⁿ = 1 / aⁿ",
  ruleZero: "a⁰ = 1 (a ≠ 0)",
  noteZeroZero:
    "0⁰ is taken to be 1, the convention used by most calculators and in combinatorics and power series. In some parts of analysis it is left undefined.",
  noteZeroNegative:
    "Zero cannot be raised to a negative power or to a negative fractional power: it would mean dividing by zero.",
  noteComplex:
    "A negative number has no real even root, so this power is not a real number. Its principal value is complex.",
  noteOddRoot:
    "The real odd root of the negative base is used, as most calculators do. The principal complex value of the power would be different.",
  noteHuge:
    "The exact result has too many digits to write out, so it is estimated with logarithms.",
  noteApproximate:
    "This irrational result is too large to compute digit by digit here, so no decimal is shown.",
  negativeRule:
    "A negative exponent means a reciprocal: {base}^(−{n}) = 1 / {base}^{n}.",
  repeated: "Multiply the base by itself: {product} = {value}.",
  binary: "Write the exponent {n} in binary: {bits}. That means {n} = {sum}.",
  squarings: "Square repeatedly: {list}.",
  combine:
    "Multiply the powers that match the binary digits: {parts} = {value}.",
  squareAndMultiply:
    "Repeated squaring, then multiplying the matching powers, gives {value}. It needs far fewer multiplications than multiplying one at a time.",
  reciprocal: "The positive power is {value}, so the reciprocal is {result}.",
  fractionalRule:
    "An exponent of {exponent} means a {root} followed by a power: {base}^{exponent} = ({root} of {base})^{p}.",
  rootSquare: "square root",
  rootCube: "cube root",
  rootIndex: "root of index {q}",
  rootIs: "The {root} of {base} is {value}.",
  rootPower: "Raise it to the power {p}: {value}^{p} = {result}.",
  irrational:
    "The {root} of {base} is not a whole number or a fraction, so the result is irrational: its decimal never ends and never repeats.",
  zeroRule: "Any nonzero number to the power 0 is 1: {base}^0 = 1.",
  oneRule: "A power of 1 leaves the base unchanged: {base}^1 = {base}.",
  hugeRule:
    "{base}^{n} is too large to write out, so it is estimated with logarithms.",
  enterBoth: "Enter a base and an exponent to see the power.",
  invalidBase: "The base is not a number. Try 2, −1.5 or 3/4.",
  invalidExponent: "The exponent is not a number. Try 10, −3, 0.5 or 2/3.",
  undefinedResult: "Undefined",
  complexResult: "Not a real number",
} as const;

type Strings = typeof EXPONENT_CALCULATOR_STRINGS;

const PLACE_CHOICES = [2, 4, 6, 8, 10, 15, 20, 30];
const DIGIT_BOX =
  "max-h-48 overflow-auto break-all rounded-lg border border-border bg-muted/30 p-4 font-mono text-foreground text-sm leading-relaxed";

function noteText(note: PowerNote, strings: Strings): string {
  switch (note) {
    case "zero-zero":
      return strings.noteZeroZero;
    case "zero-negative":
      return strings.noteZeroNegative;
    case "complex":
      return strings.noteComplex;
    case "odd-root":
      return strings.noteOddRoot;
    case "huge":
      return strings.noteHuge;
    default:
      return strings.noteApproximate;
  }
}

function Laws({ view, strings }: { view: PowerView; strings: Strings }) {
  if (view.laws.length === 0) {
    return <Notice>{strings.lawsNeedWhole}</Notice>;
  }
  const names = {
    product: [strings.lawProduct, strings.ruleProduct],
    quotient: [strings.lawQuotient, strings.ruleQuotient],
    power: [strings.lawPower, strings.rulePower],
    negative: [strings.lawNegative, strings.ruleNegative],
    zero: [strings.lawZero, strings.ruleZero],
  } as const;
  return (
    <>
      <p className="text-muted-foreground text-sm">{strings.lawsIntro}</p>
      <DataTable
        head={[strings.columnLaw, strings.columnRule, strings.columnNumbers]}
        rows={view.laws.map((row) => [
          names[row.id][0],
          names[row.id][1],
          lawExpression(view, row),
        ])}
      />
    </>
  );
}

function ResultStats({ view, strings }: { view: PowerView; strings: Strings }) {
  const { result } = view;
  if (result.kind === "undefined") {
    return <ErrorNotice>{strings.undefinedResult}</ErrorNotice>;
  }
  if (result.kind === "complex") {
    return <ErrorNotice>{strings.complexResult}</ErrorNotice>;
  }
  const summary = summarizePower(view);
  if (summary === null) {
    return null;
  }
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.result} value={summary.headline} />
        {summary.decimal ? (
          <StatCard label={strings.decimal} value={summary.decimal} />
        ) : null}
        {summary.scientific ? (
          <StatCard label={strings.scientific} value={summary.scientific} />
        ) : null}
        {summary.digits !== null && result.kind === "exact" ? (
          <StatCard label={strings.digits} value={summary.digits} />
        ) : null}
        {summary.digits !== null && result.kind === "huge" ? (
          <StatCard
            label={strings.digits}
            value={t(strings.approxDigits, { count: summary.digits })}
          />
        ) : null}
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={`${view.baseText}^${view.exponentText} = ${summary.fullDigits ?? summary.headline}`}
      />
      {summary.fullDigits ? (
        <>
          <p className="font-medium text-foreground text-sm">
            {strings.allDigits}
          </p>
          <p className={DIGIT_BOX}>{summary.fullDigits}</p>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyDigits}
            small
            text={summary.fullDigits}
          />
        </>
      ) : null}
    </>
  );
}

function Problem({
  kind,
  strings,
}: {
  kind: Exclude<PowerComputation["kind"], "ok">;
  strings: Strings;
}) {
  switch (kind) {
    case "empty":
      return <Notice>{strings.enterBoth}</Notice>;
    case "invalid-base":
      return <ErrorNotice>{strings.invalidBase}</ErrorNotice>;
    default:
      return <ErrorNotice>{strings.invalidExponent}</ErrorNotice>;
  }
}

function Outcome({
  outcome,
  strings,
}: {
  outcome: PowerComputation;
  strings: Strings;
}) {
  if (outcome.kind !== "ok") {
    return <Problem kind={outcome.kind} strings={strings} />;
  }
  const { view } = outcome;
  const working = powerWorking(view, strings);
  return (
    <>
      <ResultStats strings={strings} view={view} />
      {view.notes.map((note) => (
        <Notice key={note}>{noteText(note, strings)}</Notice>
      ))}
      {working.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
          <ul className="list-disc space-y-1 pl-6 text-foreground text-sm">
            {working.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.lawsHeading}</h3>
      <Laws strings={strings} view={view} />
    </>
  );
}

export default function ExponentCalculatorWidget() {
  const strings = useToolUi(EXPONENT_CALCULATOR_STRINGS);
  const [base, setBase] = useState("2");
  const [exponent, setExponent] = useState("10");
  const [second, setSecond] = useState("3");
  const [places, setPlaces] = useState("10");
  const outcome = useMemo(
    () => computePower(base, exponent, second, Number(places)),
    [base, exponent, second, places]
  );
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.baseHint}
          id="ex-base"
          label={strings.baseLabel}
          mono
          numeric
          onChange={setBase}
          value={base}
        />
        <TextField
          hint={strings.exponentHint}
          id="ex-exponent"
          label={strings.exponentLabel}
          mono
          numeric
          onChange={setExponent}
          value={exponent}
        />
        <TextField
          hint={strings.secondHint}
          id="ex-second"
          label={strings.secondLabel}
          mono
          numeric
          onChange={setSecond}
          value={second}
        />
        <SelectField
          id="ex-places"
          label={strings.placesLabel}
          onChange={setPlaces}
          options={PLACE_CHOICES.map((n) => ({
            value: String(n),
            label: String(n),
          }))}
          value={places}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome outcome={outcome} strings={strings} />
      </section>
    </>
  );
}
